import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import SignaturePad, { type SignaturePadHandle } from '@/components/sign/SignaturePad';
import { formatMoney, normalizeCurrency } from '@/lib/currency';
import { useLocale } from '@/lib/i18n';

// Ported from app/sign/[token]/page.tsx. The only change is the token, which
// now comes from the React Router route param instead of Next's async params.

type Clause = { title: string; body: string };
type SigSummary = { role: string; name: string; method: string; signedAt: string };

type SignPayload = {
  contract: { number: string; status: string; language: string; termsVersion: string; version: number };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  snapshot: any;
  agency: { name: string; address: string; phone: string; city: string; currency?: string };
  terms: Clause[];
  session: { verified: boolean; otpSent: boolean };
  signatures: SigSummary[];
  signerPhoneMasked: string;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'locked' }
  | { kind: 'used' }
  | { kind: 'signed'; number: string }
  | { kind: 'ready'; data: SignPayload };

const ACCENT = '#FF8A00';

function fmtUnknown(n: unknown, code?: string | null): string {
  const v = Number(n ?? 0);
  if (Number.isNaN(v)) return '—';
  return formatMoney(v, code);
}

function fmtDate(d: unknown): string {
  if (!d) return '—';
  const s = String(d);
  const dt = new Date(s.includes('T') ? s : `${s}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return s;
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function statusLabel(status: string): string {
  const m: Record<string, string> = {
    draft: 'Brouillon',
    sent: 'Envoyé',
    viewed: 'Consulté',
    signing: 'En cours de signature',
    signed: 'Signé',
    cancelled: 'Annulé',
    superseded: 'Remplacé',
  };
  return m[status] ?? status;
}

function Steps({ step, done, labels }: { step: number; done: boolean; labels: string[] }) {
  const current = done ? 4 : step;
  return (
    <ol className="flex items-center gap-1" aria-label="Progression">
      {labels.map((l, i) => {
        const n = i + 1;
        const active = n === current;
        const past = n < current;
        return (
          <li key={l} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center gap-1">
              <span
                className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
                style={{
                  backgroundColor: past || active ? ACCENT : '#e2e8f0',
                  color: past || active ? '#fff' : '#64748b',
                }}
              >
                {past ? '✓' : n}
              </span>
              <span className={`text-[11px] font-medium ${active ? 'text-slate-950' : 'text-slate-500'}`}>
                {l}
              </span>
            </div>
            {n < labels.length && <div className="mx-1 mb-5 h-0.5 flex-1 rounded bg-slate-200" />}
          </li>
        );
      })}
    </ol>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">{title}</h2>
      {children}
    </section>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-sm">
      <span className="shrink-0 text-slate-500">{k}</span>
      <span className="text-right font-medium text-slate-900">{v}</span>
    </div>
  );
}

function SignFlow({ token }: { token: string }) {
  const { t } = useLocale();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [step, setStep] = useState(1);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [phone, setPhone] = useState('');
  const [masked, setMasked] = useState('');
  const [demoCode, setDemoCode] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'idle' | 'request' | 'verify' | 'sign'>('idle');
  const [msg, setMsg] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [consent, setConsent] = useState(false);
  const [padEmpty, setPadEmpty] = useState(true);
  const padRef = useRef<SignaturePadHandle>(null);

  const [done, setDone] = useState<{ number: string; downloadUrl: string; bookingConfirmed: boolean } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/sign/${token}`, { cache: 'no-store' });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok) {
          const data = body as SignPayload;
          if (data.contract?.status === 'signed') {
            setState({ kind: 'signed', number: data.contract.number });
          } else {
            setState({ kind: 'ready', data });
            setMasked(data.signerPhoneMasked ?? '');
          }
          return;
        }
        const code = typeof body?.code === 'string' ? body.code : '';
        if (res.status === 404 || code === 'invalid') setState({ kind: 'invalid' });
        else if (res.status === 423 || code === 'locked') setState({ kind: 'locked' });
        else if (res.status === 410) {
          if (body?.signed || code === 'signed') setState({ kind: 'signed', number: String(body.number ?? '') });
          else if (code === 'expired' || String(body?.error ?? '').includes('expiré')) setState({ kind: 'expired' });
          else setState({ kind: 'used' });
        } else setState({ kind: 'invalid' });
      } catch {
        if (!cancelled) setState({ kind: 'invalid' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const data = state.kind === 'ready' ? state.data : null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const snap: any = data?.snapshot ?? {};
  const booking = snap.booking ?? {};
  const vehicle = snap.vehicle ?? {};
  const fin = snap.financials ?? {};
  const cur = normalizeCurrency(
    (data?.agency as { currency?: string } | undefined)?.currency ??
      (snap.agency as { currency?: string } | undefined)?.currency,
  );
  const fmt = (n: unknown): string => fmtUnknown(n, cur);

  async function downloadPdf() {
    const url = done?.downloadUrl ?? `/api/sign/${token}/download`;
    const res = await fetch(url);
    if (!res.ok) return;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `contrat-${done?.number ?? 'signe'}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  async function openPdf() {
    // Open the blank tab synchronously so the browser keeps the user gesture
    // and does not block the PDF viewer as a popup.
    const win = window.open('', '_blank');
    const url = done?.downloadUrl ?? `/api/sign/${token}/download`;
    const res = await fetch(url);
    if (!res.ok) {
      win?.close();
      return;
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (win) {
      win.location.href = objectUrl;
    } else {
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `contrat-${done?.number ?? 'signe'}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }

  async function requestCode() {
    setMsg(null);
    if (!phone.trim()) {
      setMsg(t('sign.needPhone'));
      return;
    }
    setBusy('request');
    try {
      const res = await fetch(`/api/sign/${token}/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(String(body?.error ?? t('sign.sendFail')));
        return;
      }
      setMasked(String(body.masked ?? ''));
      setDemoCode(typeof body.demoCode === 'string' ? body.demoCode : null);
      setMsg(t('sign.sent'));
    } catch {
      setMsg(`${t('sign.sendFail')} ${t('sign.retry')}`);
    } finally {
      setBusy('idle');
    }
  }

  async function verifyCode() {
    setMsg(null);
    if (!/^\d{6}$/.test(code.trim())) {
      setMsg(t('sign.needCode'));
      return;
    }
    setBusy('verify');
    try {
      const res = await fetch(`/api/sign/${token}/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(String(body?.error ?? t('sign.badCode')));
        return;
      }
      setStep(3);
      setMsg(null);
    } catch {
      setMsg(`${t('sign.badCode')} ${t('sign.retry')}`);
    } finally {
      setBusy('idle');
    }
  }

  async function sign() {
    setMsg(null);
    if (fullName.trim().length < 2) {
      setMsg(t('sign.needName'));
      return;
    }
    if (padRef.current?.isEmpty()) {
      setMsg(t('sign.needDraw'));
      return;
    }
    if (!consent) {
      setMsg(t('sign.needConsent'));
      return;
    }
    const signatureImage = padRef.current?.toDataURL() ?? '';
    if (!signatureImage.startsWith('data:image/png;base64,')) {
      setMsg(t('sign.badSignature'));
      return;
    }
    setBusy('sign');
    try {
      const res = await fetch(`/api/sign/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fullName.trim(),
          signatureImage,
          consent: true,
          termsVersion: data?.contract.termsVersion ?? 'v1',
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(String(body?.error ?? t('sign.signFail')));
        return;
      }
      setDone({
        number: String(body.number ?? data?.contract.number ?? ''),
        downloadUrl: String(body.downloadUrl ?? `/api/sign/${token}/download`),
        bookingConfirmed: body.bookingStatus === 'confirmed',
      });
      setMsg(null);
    } catch {
      setMsg(`${t('sign.signFail')} ${t('sign.retry')}`);
    } finally {
      setBusy('idle');
    }
  }

  return (
    <main className="theme-surface min-h-[calc(100dvh-3.5rem)] bg-slate-100">
      <div className="mx-auto w-full max-w-xl px-4 py-6">
        {/* Brand header */}
        <header className="mb-4 flex items-center gap-3">
          <span
            className="flex h-10 w-10 items-center justify-center rounded-xl text-lg font-black text-white"
            style={{ backgroundColor: '#020617' }}
          >
            D
          </span>
          <div>
            <p className="text-base font-extrabold text-slate-950">{t('sign.title')}</p>
            <p className="text-xs text-slate-500">{t('sign.secure')}</p>
          </div>
        </header>

        {state.kind === 'loading' && (
          <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500">
            {t('sign.loading')}
          </div>
        )}

        {state.kind === 'invalid' && (
          <div className="rounded-2xl border bg-white p-8 text-center">
            <p className="text-lg font-bold text-slate-950">{t('sign.invalidTitle')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('sign.invalidBody')}</p>
          </div>
        )}
        {state.kind === 'expired' && (
          <div className="rounded-2xl border bg-white p-8 text-center">
            <p className="text-lg font-bold text-slate-950">{t('sign.expiredTitle')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('sign.expiredBody')}</p>
          </div>
        )}
        {state.kind === 'used' && (
          <div className="rounded-2xl border bg-white p-8 text-center">
            <p className="text-lg font-bold text-slate-950">{t('sign.usedTitle')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('sign.usedBody')}</p>
          </div>
        )}
        {state.kind === 'locked' && (
          <div className="rounded-2xl border bg-white p-8 text-center">
            <p className="text-lg font-bold text-slate-950">{t('sign.lockedTitle')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('sign.lockedBody')}</p>
          </div>
        )}

        {(state.kind === 'signed' || done) && (
          <div className="rounded-2xl border bg-white p-8 text-center">
            <span
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full text-xl font-bold text-white"
              style={{ backgroundColor: '#16a34a' }}
            >
              ✓
            </span>
            <p className="mt-3 text-lg font-bold text-slate-950">
              {done ? t('sign.success') : t('sign.already')}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Contrat N° {(done?.number || (state.kind === 'signed' ? state.number : '')) ?? '—'}
            </p>
            {done?.bookingConfirmed && (
              <p className="mx-auto mt-2 max-w-sm rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                {t('sign.confirmed')}
              </p>
            )}
            <button
              type="button"
              onClick={() => void downloadPdf()}
              className="mt-4 inline-block rounded-xl px-5 py-3 text-sm font-bold text-white"
              style={{ backgroundColor: ACCENT }}
            >
              {t('sign.pdf')}
            </button>
            <button
              type="button"
              onClick={() => void openPdf()}
              className="mt-2 block w-full rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-700"
            >
              {t('sign.print')}
            </button>
            <p className="mt-3 text-xs text-slate-500">{t('sign.copy')}</p>
          </div>
        )}

        {data && !done && (
          <div className="space-y-4">
            <div className="rounded-2xl border bg-white p-4">
              <Steps
                step={step}
                done={false}
                labels={[t('sign.stepContract'), t('sign.stepVerify'), t('sign.stepSign'), t('sign.stepDone')]}
              />
            </div>

            {step === 1 && (
              <>
                <div className="rounded-2xl bg-slate-950 p-4 text-white">
                  <p className="text-xs uppercase tracking-wide text-slate-300">
                    {data.agency.name} — {data.agency.city}
                  </p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <h1 className="text-xl font-extrabold">Contrat N° {data.contract.number}</h1>
                    <span
                      className="rounded-full px-3 py-1 text-xs font-bold"
                      style={{ backgroundColor: ACCENT, color: '#fff' }}
                    >
                      {statusLabel(data.contract.status)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-300">
                    {data.agency.address} · {data.agency.phone}
                  </p>
                </div>

                <Card title="Parties">
                  <Row k={t('sign.client')} v={String(snap.client?.name ?? booking.customerName ?? '—')} />
                  <Row k="Téléphone" v={String(snap.client?.phone ?? booking.customerPhone ?? '—')} />
                  <Row
                    k={t('sign.license')}
                    v={String(snap.client?.licenseNumber ?? booking.driverLicense ?? snap.client?.cinPassport ?? '—')}
                  />
                </Card>

                <Card title={t('sign.vehicle')}>
                  <Row k={t('sign.vehicle')} v={String(vehicle.name ?? booking.vehicleName ?? '—')} />
                  <Row
                    k={t('sign.pickup')}
                    v={`${String(booking.pickupLocation ?? '—')} — ${fmtDate(booking.pickupDate)}`}
                  />
                  <Row
                    k={t('sign.dropoff')}
                    v={`${String(booking.dropoffLocation ?? '—')} — ${fmtDate(booking.dropoffDate)}`}
                  />
                  <Row
                    k={t('sign.duration')}
                    v={`${String(booking.rentalDays ?? snap.rentalDays ?? '—')} ${t('sign.days')}`}
                  />
                </Card>

                <Card title={t('sign.total')}>
                  <Row k={t('sign.subtotal')} v={fmt(fin.subtotal ?? booking.subtotal)} />
                  <Row k={t('sign.protection')} v={fmt(fin.protection ?? booking.protectionPrice)} />
                  <Row k={t('sign.options')} v={fmt(fin.addons ?? booking.addOnsPrice)} />
                  <div className="mt-1 border-t border-slate-100 pt-1">
                    <Row k={t('sign.total')} v={fmt(fin.total ?? booking.totalPrice)} />
                  </div>
                  <Row k={t('sign.advance')} v={fmt(fin.avance ?? booking.avanceAmount)} />
                  <Row k={t('sign.deposit')} v={fmt(fin.caution ?? booking.cautionAmount)} />
                </Card>

                <Card title={t('sign.terms')}>
                  <div className="max-h-96 space-y-3 overflow-y-auto rounded-xl bg-slate-50 p-3">
                    {data.terms.map((c, i) => (
                      <article key={i}>
                        <h3 className="text-sm font-bold text-slate-900">{c.title}</h3>
                        <p className="whitespace-pre-line text-sm text-slate-700">{c.body}</p>
                      </article>
                    ))}
                  </div>
                  <label className="mt-3 flex items-start gap-2 text-sm text-slate-800">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-[#FF8A00]"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    <span>{t('sign.accept')}</span>
                  </label>
                </Card>

                <button
                  type="button"
                  disabled={!acceptedTerms}
                  onClick={() => {
                    setMsg(null);
                    setStep(2);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="w-full rounded-xl px-5 py-3 text-sm font-bold text-white disabled:opacity-40"
                  style={{ backgroundColor: ACCENT }}
                >
                  {t('sign.continue')}
                </button>
              </>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <Card title={t('sign.phoneTitle')}>
                  {masked && (
                    <p className="text-sm text-slate-700">
                      {t('sign.phoneSent')} <strong>{masked}</strong>
                    </p>
                  )}
                  <label className="mt-2 block text-sm font-medium text-slate-700">
                    {t('sign.phoneLabel')}
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="06 XX XX XX XX"
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-base text-slate-950 outline-none focus:border-[#FF8A00]"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={busy === 'request'}
                    onClick={requestCode}
                    className="mt-2 w-full rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                  >
                    {busy === 'request' ? t('sign.sending') : t('sign.send')}
                  </button>
                  {demoCode && (
                    <p className="mt-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-bold text-amber-800">
                      {t('sign.demo')}: {demoCode}
                    </p>
                  )}
                </Card>

                <Card title={t('sign.codeTitle')}>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="••••••"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-center text-2xl font-bold tracking-[0.5em] text-slate-950 outline-none focus:border-[#FF8A00]"
                    aria-label={t('sign.codeTitle')}
                  />
                  <button
                    type="button"
                    disabled={busy === 'verify'}
                    onClick={verifyCode}
                    className="mt-2 w-full rounded-xl px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {busy === 'verify' ? t('sign.verifying') : t('sign.verify')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="mt-2 w-full rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-600"
                  >
                    {t('sign.backContract')}
                  </button>
                </Card>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <Card title={t('sign.signTitle')}>
                  <label className="block text-sm font-medium text-slate-700">
                    {t('sign.nameLabel')}
                    <input
                      type="text"
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder={t('sign.namePlaceholder')}
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-base text-slate-950 outline-none focus:border-[#FF8A00]"
                    />
                  </label>
                  <p className="mt-3 text-sm font-medium text-slate-700">{t('sign.draw')}</p>
                  <div className="mt-1">
                    <SignaturePad ref={padRef} onChange={(empty) => setPadEmpty(empty)} />
                  </div>
                  <label className="mt-2 flex items-start gap-2 text-sm text-slate-800">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-[#FF8A00]"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                    />
                    <span>{t('sign.consent')}</span>
                  </label>
                  <button
                    type="button"
                    disabled={busy === 'sign' || padEmpty || !consent || fullName.trim().length < 2}
                    onClick={sign}
                    className="mt-3 w-full rounded-xl px-5 py-3 text-sm font-bold text-white disabled:opacity-40"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {busy === 'sign' ? t('sign.signing') : t('sign.sign')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="mt-2 w-full rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-600"
                  >
                    {t('sign.backVerify')}
                  </button>
                </Card>
              </div>
            )}

            {msg && (
              <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {msg}
              </p>
            )}

            {data.signatures.length > 0 && (
              <p className="text-center text-xs text-slate-400">
                {t('sign.recorded')}:{' '}
                {data.signatures.map((s) => `${s.role} (${s.name || '—'})`).join(' · ')}
              </p>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

export default function SignPage() {
  const { token = '' } = useParams<{ token: string }>();
  return <SignFlow token={token} />;
}
